/**
 * product-split-gallery.js
 * Fuel Themes / Vision — Missoma-style split gallery
 *
 * Responsibilities:
 *   1. Scroll indicator: fades the down-arrow out when the secondary rail
 *      is scrolled to its bottom (or when there are not enough images to
 *      require scrolling). Restores it on scroll back up.
 *   2. Variant switching: when the theme dispatches a variant change event
 *      or the URL-hash changes to a specific media ID, the sticky featured
 *      image is swapped to the variant's featured media.
 *   3. Scroll isolation: the secondary rail uses CSS overflow:auto which
 *      already isolates scroll natively; no JS wheel-hijacking needed.
 */

(function () {
  'use strict';

  /* ── Utility ─────────────────────────────────────────────────────── */

  function qs(selector, root) {
    return (root || document).querySelector(selector);
  }

  /* ── Boot ─────────────────────────────────────────────────────────── */

  function init() {
    var split        = qs('.product-gallery-split');
    if (!split) return; // only runs on desktop where the split exists

    var featured     = qs('.product-gallery-split__featured', split);
    var secondary    = qs('.product-gallery-split__secondary-container', split);
    var indicator    = qs('.split-secondary__scroll-indicator');
    var featuredImg  = qs('.split-featured__img', featured);
    var featuredWrap = qs('.split-featured__media', featured);

    /* ── 1. Scroll indicator ──────────────────────────────────────── */

    if (secondary && indicator) {
      function updateIndicator() {
        var scrollBottom = secondary.scrollTop + secondary.clientHeight;
        var atBottom     = scrollBottom >= secondary.scrollHeight - 8; // 8px tolerance
        if (atBottom) {
          indicator.classList.add('is-hidden');
        } else {
          indicator.classList.remove('is-hidden');
        }
      }

      // Hide immediately if content doesn't overflow
      function checkOverflow() {
        if (secondary.scrollHeight <= secondary.clientHeight) {
          indicator.classList.add('is-hidden');
        }
      }

      secondary.addEventListener('scroll', updateIndicator, { passive: true });
      window.addEventListener('resize', checkOverflow, { passive: true });
      // Run once on load (images may not have loaded yet, so defer slightly)
      setTimeout(checkOverflow, 400);
    }

    /* ── 2. Variant switching ─────────────────────────────────────── */

    /*
     * Fuel Themes fires a custom 'variant:changed' event on the
     * document (or the product form). It carries detail.variant which
     * has .featured_media.id and the image src we need.
     *
     * We also listen for the theme's standard URL hash approach used
     * by product-slider to highlight the active slide, so we stay
     * in sync with however the theme triggers media changes.
     */

    // Map of media-id → { src, width, height, alt } built from the
    // secondary images already rendered in the DOM so we never need
    // an extra API call.
    var mediaMap = {};
    var secondaryItems = split.querySelectorAll('[data-media-id]');
    secondaryItems.forEach(function (el) {
      var id  = el.getAttribute('data-media-id');
      var img = el.querySelector('img');
      if (id && img) {
        mediaMap[id] = {
          src    : img.src,
          srcset : img.srcset || '',
          alt    : img.alt || '',
        };
      }
    });
    // Also include the featured slot itself
    if (featuredWrap && featuredImg) {
      var fid = featuredWrap.getAttribute('data-media-id');
      if (fid) {
        mediaMap[fid] = {
          src    : featuredImg.src,
          srcset : featuredImg.srcset || '',
          alt    : featuredImg.alt || '',
        };
      }
    }

    function swapFeatured(mediaId) {
      if (!featuredImg || !mediaId) return;
      var data = mediaMap[mediaId];
      if (!data) return;
      if (featuredImg.src === data.src) return; // already shown

      featuredImg.src    = data.src;
      featuredImg.srcset = data.srcset;
      featuredImg.alt    = data.alt;
    }

    // Listen for the Fuel / Vision theme's variant change event
    document.addEventListener('variant:changed', function (e) {
      var variant = e.detail && e.detail.variant;
      if (!variant) return;
      var mediaId = variant.featured_media && String(variant.featured_media.id);
      swapFeatured(mediaId);
    });

    // Also listen on the product form's change (older theme pattern)
    var productForm = qs('form[action*="/cart/add"]');
    if (productForm) {
      productForm.addEventListener('change', function () {
        // Give the theme's own JS time to set data-media-id on the
        // active slide, then read from it
        setTimeout(function () {
          var activeSlide = qs('.product-images__slide.is-selected, .product-images__slide[aria-hidden="false"]');
          if (activeSlide) {
            var mediaId = activeSlide.getAttribute('data-media-id');
            swapFeatured(mediaId);
          }
        }, 50);
      });
    }

    // Listen for Shopify's history/hash updates (theme sets URL hash = media id)
    window.addEventListener('hashchange', function () {
      var hash = window.location.hash.replace('#', '');
      if (hash && /^\d+$/.test(hash)) {
        swapFeatured(hash);
      }
    });
  }

  /* ── Entry point ─────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();